<template>
  <main class="main search-result-page" role="main">
    <div class="inner" :id="`scrollable${routePath}`" ref="scrollable">
      <div class="cate" name="카테고리 영역">
        <ul class="cate-ul" ref="container">
          <li :class="currentCategory === null ? 'cate-li on' : 'cate-li'" @click="selectCategory(null)">
            {{ $t('admin.dashboard.section.order_rank.all') }}
          </li>
          <li
            :class="currentCategory === item.storeCategoryNo ? 'cate-li on' : 'cate-li'"
            v-for="item in categoryList"
            :key="`category_${item.storeCategoryNo}`"
            @click="selectCategory(item.storeCategoryNo)"
          >
            {{ Util.translator(item, 'category') }}
          </li>
        </ul>
      </div>

      <div
        class="search-result-store-area"
        :class="{ 'is-category-transition-loading': showCategoryLoader }"
        @touchstart.passive="onCategoryTouchStart"
        @touchmove.passive="onCategoryTouchMove"
        @touchend.passive="onCategoryTouchEnd"
        @touchcancel="onCategoryTouchCancel"
        @pointerdown="onCategoryPointerDown"
        @pointermove="onCategoryPointerMove"
        @pointerup="onCategoryPointerUp"
        @pointercancel="onCategoryPointerCancel"
        @pointerleave="onCategoryPointerCancel"
        @click.capture="onStoreAreaClickCapture"
      >
        <div
          v-show="showCategoryLoader"
          class="search-result-store-loading-anchor"
          role="status"
          aria-label="Loading stores"
        >
          <div class="loading-process-page">
            <div class="img-wrap">
              <img src="@/assets/static/images/car.gif" alt="" aria-hidden="true" />
            </div>
          </div>
        </div>
        <div class="store search-result-store" name="추천 상점">
          <div class="search-result-store-header">
            <h3>{{ $t('search.result.section.recommend_store') }}</h3>
            <ul class="filter-ul">
              <!--          정렬 필터-->
              <li
                class="filter-li"
                :class="{
                  on:
                    filterState['STORE_ORDER_BY_CONDITION'] !== 'STORE_ORDER_REG_DATE' &&
                    filterState['STORE_ORDER_BY_CONDITION']
                }"
                @click="filter(STORE_ORDER_BY_CONDITION.constant)"
              >
                <i class="i-nomal" />{{
                  filterState['STORE_ORDER_BY_CONDITION'] &&
                  filterState['STORE_ORDER_BY_CONDITION'] !== 'STORE_ORDER_REG_DATE'
                    ? pickOrderFilterLabel(filterState['STORE_ORDER_BY_CONDITION'])
                    : $t('search.result.filters.basic')
                }}
              </li>
            </ul>
          </div>
          <ul
            class="store-ul"
            ref="recommendedListElement"
            :class="categoryMotionClass"
            @animationend.self="categoryMotionDirection = ''"
          >
            <template v-if="displayedRecommendedStoreList.length === 0">
              <li>
                <NoData :message="$t('search.result.section.no_recommended_msg')" />
              </li>
            </template>

            <template v-else>
              <li class="store-li" v-for="item in displayedRecommendedStoreList" :key="`recommend_${item.storeNo}`">
                <SearchStoreCard :user-position="userPosition" :store-info="item" />
              </li>
            </template>
          </ul>
        </div>

        <div class="bar1"></div>

        <div class="store search-result-store" name="일반 상점 영역" v-if="visibleStoreList.length !== 0">
          <h3>{{ $t('search.result.section.normal_store') }}</h3>
          <ul
            class="store-ul"
            ref="normalListElement"
            :class="categoryMotionClass"
            @animationend.self="categoryMotionDirection = ''"
          >
            <li class="store-li" v-for="item in visibleStoreList" :key="`normal_${item.storeNo}`">
              <SearchStoreCard :user-position="userPosition" :store-info="item" />
            </li>
          </ul>
        </div>

        <!--          INFINITE ON -->
        <div
          v-if="isRecommendReady"
          class="infinite"
          :key="infinite"
          v-observe-visibility="{ callback: fetch, throttle: 100 }"
        ></div>
      </div>
    </div>
  </main>
</template>

<script setup>
import NoData from '@/components/views/nodata/index';
import { LOCALE_MAP, STORE_CONDITION_TYPE } from '@/common/constants/ConstCode';
import { modalList, show } from '@/common/utils/ControlModal';
import { computed, nextTick, onActivated, onBeforeMount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Util from '@/common/utils/Util';
import { recoverScroll } from '@/common/utils/ScrollManager';
import SearchStoreCard from '@/views/search/SearchStoreCard.vue';
import {
  createCategoryDragFeedback,
  createCategorySwipeTracker,
  createCategoryTransitionGuard,
  createSwipeClickSuppressor,
  getAdjacentCategoryNo,
  getCategoryTransitionDirection
} from '@/views/store/search/categorySwipe';

const vuexStore = useStore();
const route = useRoute();
const router = useRouter();
const { dispatch, getters, commit } = vuexStore;
const { STORE_ORDER_BY_CONDITION } = STORE_CONDITION_TYPE;

const parseStoreCategoryNo = (value) => {
  const categoryNo = Number(value);
  return Number.isFinite(categoryNo) ? categoryNo : null;
};

const routePath = computed(() => route.path);
const locale = computed(() => getters['locale/getLocale']);
const infinite = computed(() => getters['store/getInfiniteKey']);
const currentCategory = ref(parseStoreCategoryNo(route.params.storeCategoryNo));
const userPosition = computed(() => getters['user/getUserPosition']);
const categoryList = computed(() => getters['main/getCategoryList']);
const selectionList = computed(() => getters['constant/getConstant']['STORE_ORDER_BY_CONDITION']);
const filterState = computed(() => getters['search/getFilter']);
const storeList = computed(() => getters['store/getStoreList']);
const recommendedStoreList = computed(() => getters['store/getRecommendedStoreList']);
const retainedStoreList = ref(null);
const retainedRecommendedStoreList = ref(null);
const displayedStoreList = computed(() => retainedStoreList.value ?? storeList.value);
const displayedRecommendedStoreList = computed(() => retainedRecommendedStoreList.value ?? recommendedStoreList.value);
const recommendedStoreNoSet = computed(() => new Set(displayedRecommendedStoreList.value.map((item) => item.storeNo)));
const visibleStoreList = computed(() => {
  return displayedStoreList.value.filter((item) => !recommendedStoreNoSet.value.has(item.storeNo));
});
const swipeCategories = computed(() => [{ storeCategoryNo: null }, ...categoryList.value]);

const isRecommendReady = ref(false);
const isInitialized = ref(false);
const isCategoryTransitionLoading = ref(false);
const isSwipeHandoff = ref(false);
const showCategoryLoader = computed(() => isCategoryTransitionLoading.value && !isSwipeHandoff.value);
const recommendedListElement = ref(null);
const normalListElement = ref(null);
const categoryMotionDirection = ref('');
const categoryMotionClass = computed(() =>
  categoryMotionDirection.value ? `category-list-enter-${categoryMotionDirection.value}` : ''
);
const lastVisitedPath = ref('');
let pendingStoreFetch = Promise.resolve();
let isStorePageFetching = false;

const syncCategoryFromRoute = () => {
  currentCategory.value = parseStoreCategoryNo(route.params.storeCategoryNo);
};

const buildCategoryPayload = () => {
  if (currentCategory.value === null) return {};
  return { storeCategoryNo: currentCategory.value };
};

const resetResultState = () => {
  isRecommendReady.value = false;
  commit('store/initStoreList');
  commit('store/initRecommendedStoreList');
};

const initializeResultPage = async () => {
  categoryMotionDirection.value = '';
  syncCategoryFromRoute();
  resetResultState();

  await dispatch('main/fetchStoreCategoryList');
  await fetchRecommend();
  commit('store/updateInfiniteKey');
  await nextTick();

  if (currentCategory.value !== null) {
    const currentIndex = categoryList.value.map((item) => item.storeCategoryNo).indexOf(currentCategory.value);
    await scrollToCenter(currentIndex);
  }

  lastVisitedPath.value = route.fullPath;
};

onBeforeMount(async () => {
  await initializeResultPage();
  isInitialized.value = true;
});

onActivated(async () => {
  if (!isInitialized.value || isCategoryTransitionLoading.value) return;

  if (lastVisitedPath.value !== route.fullPath) {
    await initializeResultPage();
    return;
  }

  recoverScroll(routePath.value);
});

watch(
  () => filterState.value,
  (_, previous) => {
    if (!isInitialized.value || !previous) return;
    categoryMotionDirection.value = '';
    isRecommendReady.value = false;
  },
  { deep: true }
);

watch(
  () => infinite.value,
  () => {
    if (!isInitialized.value) return;
    isRecommendReady.value = true;
  }
);

const fetchRecommend = async () => {
  await dispatch('store/fetchRecommendedStoreList', {
    ...buildCategoryPayload(),
    limit: 20,
    isRandom: true
  });
  isRecommendReady.value = true;
};

const fetch = async (isVisible) => {
  if (!isVisible || !isRecommendReady.value || isCategoryTransitionLoading.value || isStorePageFetching) return;

  isStorePageFetching = true;
  try {
    const request = dispatch('store/fetchStoreList', {
      ...buildCategoryPayload()
    });
    pendingStoreFetch = request.catch(() => {});
    await request;
  } finally {
    isStorePageFetching = false;
  }
};

const pickOrderFilterLabel = (item) => {
  const lang = LOCALE_MAP[locale.value] || 'Kr';
  return selectionList.value[item]?.[lang] || selectionList.value[item]?.Kr || '';
};

const filter = async (constant) => {
  if (isCategoryTransitionLoading.value) return;
  if (constant === 'RESET') {
    commit('search/resetFilter');
    await initializeResultPage();
    return;
  }

  if (constant === 'STORE_ETC_CONDITION') {
    show(modalList.BottomCheck, { constantType: constant });
  } else {
    show(modalList.BottomRadio, { constantType: constant });
  }
};

const transitionCategory = async (categoryNo) => {
  const normalizedCategory = categoryNo === null ? null : Number(categoryNo);
  if (!isInitialized.value || normalizedCategory === currentCategory.value) return;

  const previousCategory = currentCategory.value;
  const direction = getCategoryTransitionDirection(
    swipeCategories.value,
    previousCategory,
    normalizedCategory,
    'storeCategoryNo'
  );
  const previousPath = route.fullPath;
  const wasRecommendReady = isRecommendReady.value;
  let previousResults = null;
  let completed = false;

  categoryMotionDirection.value = '';
  isCategoryTransitionLoading.value = true;
  isRecommendReady.value = false;

  try {
    await nextTick();
    await pendingStoreFetch;

    previousResults = {
      stores: [...storeList.value],
      recommended: [...recommendedStoreList.value],
      storePageInfo: { ...getters['store/getStorePageInfo'] },
      recommendedPageInfo: { ...getters['store/getRecommendedPageInfo'] }
    };
    retainedStoreList.value = previousResults.stores;
    retainedRecommendedStoreList.value = previousResults.recommended;

    currentCategory.value = normalizedCategory;
    resetResultState();

    await dispatch('main/fetchStoreCategoryList', { notLoading: true });
    const recommendedResult = await dispatch('store/fetchRecommendedStoreList', {
      ...buildCategoryPayload(),
      limit: 20,
      isRandom: true,
      notLoading: true
    });
    if (recommendedResult === false) throw new Error('Recommended stores could not be loaded');

    const storeResult = await dispatch('store/fetchStoreList', {
      ...buildCategoryPayload(),
      notLoading: true
    });
    if (storeResult === false) throw new Error('Stores could not be loaded');

    const targetPath = normalizedCategory === null ? '/search/result' : `/search/result/${normalizedCategory}`;
    const navigationFailure = await router.replace(targetPath);
    if (navigationFailure) throw navigationFailure;
    lastVisitedPath.value = route.fullPath;
    await nextTick();

    if (normalizedCategory !== null) {
      const currentIndex = categoryList.value.findIndex((item) => item.storeCategoryNo === normalizedCategory);
      await scrollToCenter(currentIndex);
    }

    completed = true;
  } catch (error) {
    currentCategory.value = previousCategory;
    if (previousResults) {
      commit('store/setStorePageInfo', previousResults.storePageInfo);
      commit('store/setRecommendedPageInfo', previousResults.recommendedPageInfo);
      commit('store/fetchStoreList', previousResults.stores);
      commit('store/setRecommendedStoreList', previousResults.recommended);
    }
    if (route.fullPath !== previousPath) await router.replace(previousPath);
    lastVisitedPath.value = previousPath;
    throw error;
  } finally {
    retainedStoreList.value = null;
    retainedRecommendedStoreList.value = null;
    if (isSwipeHandoff.value) finishSwipeHandoff();
    if (completed) categoryMotionDirection.value = direction;
    isCategoryTransitionLoading.value = false;
    isRecommendReady.value = completed || wasRecommendReady;
    if (completed) commit('store/updateInfiniteKey');
  }
};

const selectCategory = createCategoryTransitionGuard(transitionCategory);
const categorySwipeTracker = createCategorySwipeTracker();
const categoryDragFeedback = createCategoryDragFeedback(() => [recommendedListElement.value, normalListElement.value]);
const swipeClickSuppressor = createSwipeClickSuppressor();
let activeTouchId = null;
let activePointerId = null;
let hadHorizontalDrag = false;
let swipeHandoffTimer = null;

const finishSwipeHandoff = () => {
  if (swipeHandoffTimer !== null) window.clearTimeout(swipeHandoffTimer);
  swipeHandoffTimer = null;
  isSwipeHandoff.value = false;
  categoryDragFeedback.clear();
};

const startCategoryGesture = (point) => {
  categoryMotionDirection.value = '';
  categoryDragFeedback.clear();
  hadHorizontalDrag = false;
  categorySwipeTracker.start(point);
};

const moveCategoryGesture = (point) => {
  const { axis, offset } = categorySwipeTracker.drag(point);
  if (axis === 'horizontal') {
    hadHorizontalDrag = true;
    categoryDragFeedback.drag(offset);
  } else {
    categoryDragFeedback.clear();
    if (axis === 'vertical') hadHorizontalDrag = false;
  }
};

const endCategoryGesture = (point) => {
  moveCategoryGesture(point);
  const direction = categorySwipeTracker.end(point);
  if (!direction) {
    if (hadHorizontalDrag) swipeClickSuppressor.suppress();
    categoryDragFeedback.reset();
    hadHorizontalDrag = false;
    return;
  }

  swipeClickSuppressor.suppress();
  hadHorizontalDrag = false;
  const adjacentCategoryNo = getAdjacentCategoryNo(swipeCategories.value, currentCategory.value, direction, 'storeCategoryNo');
  if (adjacentCategoryNo === undefined) return categoryDragFeedback.reset();

  isSwipeHandoff.value = true;
  categoryDragFeedback.commit(direction);
  swipeHandoffTimer = window.setTimeout(finishSwipeHandoff, 120);
  return selectCategory(adjacentCategoryNo).then((accepted) => {
    if (!accepted) {
      window.clearTimeout(swipeHandoffTimer);
      swipeHandoffTimer = null;
      isSwipeHandoff.value = false;
      categoryDragFeedback.reset();
    }
  });
};

const cancelCategoryGesture = () => {
  categorySwipeTracker.cancel();
  if (hadHorizontalDrag) categoryDragFeedback.reset();
  else categoryDragFeedback.clear();
  hadHorizontalDrag = false;
};

const onCategoryTouchStart = (event) => {
  if (!isInitialized.value || isCategoryTransitionLoading.value || event.touches.length !== 1) {
    onCategoryTouchCancel();
    return;
  }

  const touch = event.touches[0];
  activeTouchId = touch.identifier;
  startCategoryGesture({ x: touch.clientX, y: touch.clientY });
};

const onCategoryTouchMove = (event) => {
  if (activeTouchId === null) return;
  if (event.touches.length !== 1 || event.touches[0].identifier !== activeTouchId) {
    onCategoryTouchCancel();
    return;
  }

  const touch = event.touches[0];
  moveCategoryGesture({ x: touch.clientX, y: touch.clientY });
};

const onCategoryTouchEnd = (event) => {
  if (activeTouchId === null) return;
  const touch = event.changedTouches[0];
  if (event.touches.length !== 0 || touch?.identifier !== activeTouchId) {
    onCategoryTouchCancel();
    return;
  }

  activeTouchId = null;
  return endCategoryGesture({ x: touch.clientX, y: touch.clientY });
};

const onCategoryTouchCancel = () => {
  activeTouchId = null;
  cancelCategoryGesture();
};

const onCategoryPointerDown = (event) => {
  if (event.pointerType === 'touch' || event.button !== 0 || event.isPrimary === false) return;
  if (!isInitialized.value || isCategoryTransitionLoading.value) return;
  activePointerId = event.pointerId;
  startCategoryGesture({ x: event.clientX, y: event.clientY });
};

const onCategoryPointerMove = (event) => {
  if (event.pointerId !== activePointerId) return;
  moveCategoryGesture({ x: event.clientX, y: event.clientY });
};

const onCategoryPointerUp = (event) => {
  if (event.pointerId !== activePointerId) return;
  activePointerId = null;
  return endCategoryGesture({ x: event.clientX, y: event.clientY });
};

const onCategoryPointerCancel = (event) => {
  if (event.pointerId !== activePointerId) return;
  activePointerId = null;
  cancelCategoryGesture();
};

const onStoreAreaClickCapture = (event) => {
  if (!swipeClickSuppressor.shouldSuppress()) return;
  event.preventDefault();
  event.stopPropagation();
};

const container = ref(null);

// MEMO : 선택한 카테고리를 리스트 중앙으로 스크롤 이동
const scrollToCenter = async (index) => {
  if (index === null || index === undefined || index < 0 || !container.value) return;

  // 카테고리 리스트 warp DOM
  const containerRect = container.value.getBoundingClientRect();

  // 선택된 카테고리 DOM
  const selectedEl = document.getElementsByClassName('cate-li on')[0];

  if (selectedEl) {
    // 선택된 카테고리 DOM의 위치
    const elementRect = selectedEl.getBoundingClientRect();

    // 선택된 카테고리가 화면 중앙에 오도록 계산
    const offset = elementRect.left - containerRect.width / 2 + elementRect.width / 2;

    // 스크롤 이동
    container.value.scrollBy({
      left: offset,
      behavior: 'smooth'
    });
  }
};
</script>
<script>
export default {
  name: 'SEARCH_RESULT'
};
</script>
<style scoped>
.main > .inner {
  padding: 1rem 1rem;
  font-size: 1.4rem;
}

.cate-ul {
  overflow-x: auto;
}

.cate-li.on {
  flex: 0 0 auto;
  padding: 1rem;
  cursor: pointer;
}

.search-result-store {
  padding: 1rem;
  padding-top: 0;
}

.search-result-store-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
}

.search-result-store-header h3 {
  margin-top: 0;
}

.search-result-store-header .filter-ul {
  flex: 0 0 auto;
  padding: 0;
  overflow: visible;
}

.search-result-store-header .filter-li {
  margin: 0;
  padding: 0.3rem 0.6rem;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
}

.search-result-store .store-ul {
  display: flex;
  flex-direction: column;
  margin-top: 1.2rem;
  gap: 1rem;
}

.search-result-store .store-ul > .store-li,
.search-result-store .store-ul > .store-li:not(:last-child) {
  min-width: 0;
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: 0;
}
</style>

<style scoped lang="scss">
.search-result-page {
  box-sizing: border-box;
  min-width: 0;
  background: #fff;
  color: #26313b;
  overflow-x: hidden;
}

.search-result-page > .inner {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 1rem;
  overflow-x: hidden;
}

.search-result-page .cate {
  min-width: 0;
  margin: 0;
}

.search-result-page .cate-ul {
  display: flex;
  gap: 0.6rem;
  min-width: 0;
  margin: 0;
  padding: 0.2rem 0 0.8rem;
  overflow-x: auto;
  scrollbar-width: none;
}

.search-result-page .cate-ul::-webkit-scrollbar {
  display: none;
}

.search-result-page .cate-li,
.search-result-page .cate-li.on {
  flex: 0 0 auto;
  margin: 0;
  padding: 0.8rem 1.2rem;
  border: 1px solid #e0e7ea;
  border-radius: 999px;
  background: #fff;
  color: #697780;
  font-size: 1.3rem;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  cursor: pointer;
}

.search-result-page .cate-li.on {
  border-color: #00aedd;
  background: #e5f8fc;
  color: #008da9;
  font-weight: 700;
}

.search-result-page .search-result-store-area {
  position: relative;
  min-height: 100%;
}

.search-result-page .search-result-store-area.is-category-transition-loading::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  background: rgba(232, 236, 239, 0.55);
  pointer-events: none;
}

.search-result-page .category-list-enter-next {
  animation: store-category-enter-next 180ms ease-out;
}

.search-result-page .category-list-enter-previous {
  animation: store-category-enter-previous 180ms ease-out;
}

@keyframes store-category-enter-next {
  from {
    transform: translateX(18px);
    opacity: 0.9;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes store-category-enter-previous {
  from {
    transform: translateX(-18px);
    opacity: 0.9;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-result-page .category-list-enter-next,
  .search-result-page .category-list-enter-previous {
    animation: none;
  }
}

.search-result-page .search-result-store-loading-anchor {
  position: sticky;
  top: 50%;
  z-index: 2;
  height: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.search-result-page .search-result-store-loading-anchor .loading-process-page {
  height: auto;
  transform: translateY(-50%);
}

.search-result-page .search-result-store-loading-anchor .img-wrap {
  margin-top: 0;
  width: 13rem;
}

.search-result-page .search-result-store-loading-anchor img {
  display: block;
  width: 100%;
  height: auto;
}

.search-result-page .search-result-store {
  margin: 1rem 0 0;
  padding: 0;
}

.search-result-page .search-result-store-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
  margin: 0;
  padding: 0;
}

.search-result-page .search-result-store-header h3 {
  min-width: 0;
  margin: 0;
  color: #26313b;
  font-size: 1.6rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.search-result-page .search-result-store-header .filter-ul {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
  overflow: visible;
}

.search-result-page .search-result-store-header .filter-li {
  margin: 0;
  padding: 0.7rem 1rem;
  border: 1px solid #e0e7ea;
  border-radius: 999px;
  background: #fff;
  color: #52636d;
  font-size: 1.2rem;
  line-height: 1.3;
  white-space: nowrap;
  cursor: pointer;
}

.search-result-page .search-result-store-header .filter-li.on {
  border-color: #00aedd;
  background: #e5f8fc;
  color: #008da9;
}

.search-result-page .search-result-store .store-ul {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  margin: 1rem 0 0;
  padding: 0;
}

.search-result-page .search-result-store .store-ul > .store-li,
.search-result-page .search-result-store .store-ul > .store-li:not(:last-child) {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

@media (max-width: 42rem) {
  .search-result-page .search-result-store-header {
    align-items: flex-start;
  }

  .search-result-page .search-result-store-header .filter-li {
    padding: 0.6rem 0.8rem;
  }
}
</style>

/* *Created on 2023-08-28 *ProjectName user */
